/*
 * @providesModule LoadingIndicator
 *
 */

import React from 'react';

import AppIcon from 'AppIcon';
import Label from 'Label';
import LinearGradient from 'react-native-linear-gradient';
import Optional from 'Optional';
import SkillzStyle from 'SkillzStyle';

import {Easing, Platform, View, Animated, StyleSheet} from 'react-native';

const {fontPrimary, fontSizeH1, textColorPrimary, viewPrimaryColor, viewCornerRadiusPrimary} =
  SkillzStyle.sheetStyles;

let sLoadingIndicator: LoadingIndicator | null = null;
let delayTimeout: NodeJS.Timeout | null = null;

type Props = {
  titleText: string;
  indicatorStyle: string;
  isInline: boolean;
  testID: string;
};

type State = {
  isBusy: boolean;
  backgroundOpacity: Animated.Value | number;
  firstAnimatedValue: Animated.Value;
  secondAnimatedValue: Animated.Value;
  thirdAnimatedValue: Animated.Value;
};
export default class LoadingIndicator extends React.Component<Props, State> {
  static Style = {
    FullScreen: 'FullScreen',
    Small: 'Small',
    NoIcon: 'NoIcon',
    Invisible: 'Invisible',
  };

  static busyLevel = 0;
  static disabled = false;
  showIcon: boolean;
  text: any;
  timer: NodeJS.Timeout | null;
  dotsAnimation: any;
  isInvisible: boolean | undefined;
  showBackground: boolean | undefined;
  openingAnimation: Animated.CompositeAnimation | null | undefined;

  constructor(props: any) {
    super(props);

    this.showIcon = false;
    /* eslint-disable no-magic-numbers */
    const firstAnimatedValue = new Animated.Value(0);
    const secondAnimatedValue = new Animated.Value(0);
    const thirdAnimatedValue = new Animated.Value(0);

    let backgroundOpacity: number | Animated.Value = 1;
    if (!props.isInline) {
      sLoadingIndicator = this;
      backgroundOpacity = new Animated.Value(0.01);
    } else {
      this.text = this.props.titleText;
    }

    this.timer = null;

    this.state = {
      isBusy: false,
      backgroundOpacity: backgroundOpacity,
      firstAnimatedValue,
      secondAnimatedValue,
      thirdAnimatedValue,
    };
  }

  componentDidMount() {
    if (this.props.isInline) {
      this._animateDots();
    }
  }

  componentWillUnmount() {
    if (this.props.isInline) {
      this.dotsAnimation.stop();
    }
  }

  static show(text: string = '', indicatorStyle: string = LoadingIndicator.Style.Small) {
    if (LoadingIndicator.disabled) {
      return;
    }

    if (delayTimeout) {
      clearTimeout(delayTimeout);
    }
    delayTimeout = setTimeout(() => {
      ++LoadingIndicator.busyLevel;

      if (LoadingIndicator.busyLevel == 1 && sLoadingIndicator) {
        sLoadingIndicator._show(text, indicatorStyle);
      }
    }, 300);
  }

  // See SkillzSDK showModal for explanation here.
  static showInvisible() {
    LoadingIndicator.show('', LoadingIndicator.Style.Invisible);
  }

  static hideInvisible() {
    if (sLoadingIndicator) {
      LoadingIndicator.busyLevel = 0;
      // Do NOT clear isInvisible here. _hide() fades backgroundOpacity 1.0 -> 0.01
      // over 150ms and only flips isBusy=false at the end, so any render during that
      // window uses `opacity = isInvisible ? 0 : backgroundOpacity`. Clearing
      // isInvisible first made an *invisible* indicator paint (Skillz icon + 3 dots)
      // at full opacity for the whole hide — e.g. flashing over a modal that
      // showInvisible() had blocked. Leaving it true keeps opacity pinned to 0
      // through the hide; the next _show() sets isInvisible per its own style.
      sLoadingIndicator._hide();
    }
  }

  static showWithText(text: string, indicatorStyle: string = LoadingIndicator.Style.Small) {
    LoadingIndicator.show(text, indicatorStyle);
  }

  static forceHide() {
    if (delayTimeout) {
      clearTimeout(delayTimeout);
    }
    LoadingIndicator.busyLevel = 0;
    if (sLoadingIndicator) {
      sLoadingIndicator._hide();
    }
  }

  static hide() {
    if (delayTimeout) {
      clearTimeout(delayTimeout);
    }
    if (LoadingIndicator.busyLevel > 0) {
      --LoadingIndicator.busyLevel;

      if (LoadingIndicator.busyLevel == 0 && sLoadingIndicator) {
        sLoadingIndicator._hide();
      }
    }
  }

  // Allows to temporarily disable LoadingIndicator without updating Controllers logic
  // in case custom loading state is preferred
  static disable() {
    LoadingIndicator.disabled = true;
    LoadingIndicator.forceHide();
  }

  static enable() {
    LoadingIndicator.disabled = false;
  }

  _show(text: string, indicatorStyle: string) {
    this.isInvisible = indicatorStyle == LoadingIndicator.Style.Invisible;
    this.showIcon = indicatorStyle !== LoadingIndicator.Style.NoIcon;

    if (Platform.OS === 'ios' || Platform.OS === 'web') {
      if (!this.state.isBusy) {
        this.setState({isBusy: true});

        this.text = text;
        this.showBackground = true;

        if (indicatorStyle != LoadingIndicator.Style.FullScreen) {
          this.showBackground = false;
        }

        if (!this.props.isInline) {
          this._completeSingletonDisplay();
        } else {
          this._animateDots();
        }

        this.forceUpdate();
      }
    } else {
      this.timer = setTimeout(() => {
        if (!this.state.isBusy && LoadingIndicator.busyLevel > 0) {
          this.setState({isBusy: true});

          this.text = text;
          this.showBackground = true;

          if (indicatorStyle != LoadingIndicator.Style.FullScreen) {
            this.showBackground = false;
          }

          if (!this.props.isInline) {
            this._completeSingletonDisplay();
          } else {
            this._animateDots();
          }
          this.timer = null;
          this.forceUpdate();
        }
      }, 100);
    }
  }

  _completeSingletonDisplay() {
    if (this.state.backgroundOpacity instanceof Animated.Value) {
      this.state.backgroundOpacity.setValue(0.01);

      this.openingAnimation = Animated.timing(this.state.backgroundOpacity, {
        fromValue: 0.01,
        toValue: 1.0,
        duration: 150,
        useNativeDriver: true,
      });

      this.openingAnimation.start(() => {
        this.openingAnimation = null;
        this._animateDots();
      });
    }
  }

  _hide() {
    if (this.openingAnimation != null) {
      setTimeout(() => {
        this._hide();
      }, 300);
      return;
    }
    if (!this.props.isInline && this.state.backgroundOpacity instanceof Animated.Value) {
      Animated.timing(this.state.backgroundOpacity, {
        fromValue: 1.0,
        toValue: 0.01,
        duration: 150,
        useNativeDriver: true,
      }).start(() => {
        this.setState({isBusy: false});
      });
    }
  }

  _animateDots() {
    const animations = [];

    animations.push(this._animateSingleDot(this.state.firstAnimatedValue));
    animations.push(this._animateSingleDot(this.state.secondAnimatedValue));
    animations.push(this._animateSingleDot(this.state.thirdAnimatedValue));

    this.dotsAnimation = Animated.stagger(333, animations);
    this.dotsAnimation.start();
  }

  _animateSingleDot(animatedValue: Animated.Value): Animated.CompositeAnimation {
    return Animated.loop(
      Animated.timing(animatedValue, {
        fromValue: 0,
        toValue: 1.0,
        easing: Easing.easeInEaseOut,
        useNativeDriver: true,
        duration: 1000,
      })
    );
  }

  _renderAppIcon(): React.ReactNode | null {
    if (this.showIcon) {
      return <AppIcon style={styles.appIcon} />;
    }
    return null;
  }

  _dynamicDotContainerPadding(): Object {
    let marginTop = 0;
    let marginBottom = 0;
    if (!this.showIcon && !this.text) {
      marginTop = 28;
      marginBottom = 28;
    } else if (this.text) {
      marginTop = 18;
      marginBottom = 18;
    } else {
      marginTop = 12;
      marginBottom = 18;
    }

    return {
      marginTop: marginTop,
      marginBottom: marginBottom,
    };
  }

  _dynamicTransform(): {transform: {scale: number}[]} {
    if (this.showBackground) {
      return {transform: [{scale: 1}]};
    }
    return {transform: [{scale: 0.7}]};
  }

  _renderBackground(): React.ReactNode {
    return (
      <LinearGradient
        style={StyleSheet.absoluteFill}
        colors={SkillzStyle.gradients.backgroundViewGradient}
      />
    );
  }

  render(): React.ReactNode {
    if (!this.state.isBusy && !this.props.isInline) {
      return false;
    }

    const ViewType = this.props.isInline ? View : Animated.View;

    const dotContainerPadding = this._dynamicDotContainerPadding();
    const opacityInterpolation = {
      inputRange: [0, 0.5, 0.75, 1.0],
      outputRange: [0.25, 1.0, 0.25, 0.25],
    };
    const scalingInterpolation = {
      inputRange: [0, 0.5, 0.75, 1.0],
      outputRange: [0.5, 1.0, 0.5, 0.5],
    };
    const opacity = this.isInvisible ? 0 : this.state.backgroundOpacity;
    const pointerEvents = this.isInvisible ? 'none' : 'auto';

    return (
      <ViewType style={[styles.background, {opacity: opacity, pointerEvents: pointerEvents}]}>
        {this.showBackground && this._renderBackground()}

        <View
          style={[styles.container, this._dynamicTransform()]}
          testID={this.props.testID}
          accessibilityLabel={'loading_dots'}
        >
          {this._renderAppIcon()}
          <View style={[styles.dotsContainer, dotContainerPadding]}>
            {/* Dot 1 */}
            <Animated.View
              style={[
                styles.dot,
                {
                  transform: [
                    {
                      scale: this.state.firstAnimatedValue.interpolate(scalingInterpolation),
                    },
                  ],
                  opacity: this.state.firstAnimatedValue.interpolate(opacityInterpolation),
                },
              ]}
            />

            {/* Dot 2 */}
            <Animated.View
              style={[
                styles.dot,
                {
                  marginHorizontal: 14,
                  transform: [
                    {
                      scale: this.state.secondAnimatedValue.interpolate(scalingInterpolation),
                    },
                  ],
                  opacity: this.state.secondAnimatedValue.interpolate(opacityInterpolation),
                },
              ]}
            />

            {/* Dot 3 */}
            <Animated.View
              style={[
                styles.dot,
                {
                  transform: [
                    {
                      scale: this.state.thirdAnimatedValue.interpolate(scalingInterpolation),
                    },
                  ],
                  opacity: this.state.thirdAnimatedValue.interpolate(opacityInterpolation),
                },
              ]}
            />
          </View>

          {/* Label */}
          <Optional test={this.text && this.text != ''}>
            <Label style={styles.labelText} key={'title'}>
              {this.text}
            </Label>
          </Optional>
        </View>
      </ViewType>
    );
  }
}

const styles = StyleSheet.create({
  appIcon: {
    alignSelf: 'center',
    width: 90,
    height: 90,
    marginTop: 20,
    borderRadius: 6,
  },

  background: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        zIndex: 10100,
      },
    }),
  },

  container: {
    width: 160,
    marginBottom: 20,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    backgroundColor: SkillzStyle.convertToOpacity(
      viewPrimaryColor.backgroundColor,
      SkillzStyle.opacityLevels.opacityTertiary
    ),
    ...viewCornerRadiusPrimary,
  },

  dot: {
    backgroundColor: textColorPrimary.color,
    borderRadius: 10,
    width: 20,
    height: 20,
  },

  dotsContainer: {
    marginHorizontal: 36,
    height: 20,
    flexDirection: 'row',
  },

  labelText: {
    textAlign: 'center',
    marginBottom: 10,
    marginTop: -10,
    marginHorizontal: 5,
    ...fontPrimary,
    ...fontSizeH1,
    ...textColorPrimary,
  },
});
